import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { verifyCredential, getFileUrl } from '../lib/api';
import { Loader2, ShieldCheck, ShieldAlert, ArrowLeft, Search } from 'lucide-react';

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
};

export default function VerifyCredentialPage() {
  const { code: paramCode } = useParams();
  const nav = useNavigate();
  const [code, setCode] = useState(paramCode || '');
  const [input, setInput] = useState(paramCode || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const fetchVerify = useCallback(async (c) => {
    if (!c) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const { data } = await verifyCredential(c.trim().toUpperCase());
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No se pudo verificar la credencial');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (paramCode) fetchVerify(paramCode);
  }, [paramCode, fetchVerify]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const v = (input || '').trim().toUpperCase();
    if (!v) return;
    setCode(v);
    nav(`/verificar/${encodeURIComponent(v)}`);
  };

  const valid = result && result.valid;
  const notFound = result && !result.valid && result.reason === 'not_found';
  const expired = result && !result.valid && result.reason === 'expired';
  const photoSrc = result?.photo_url ? (result.photo_url.startsWith('http') ? result.photo_url : getFileUrl(result.photo_url)) : null;
  const roleLabel = result?.role === 'admin' ? 'ADMINISTRADOR' : 'PERIODISTA';

  return (
    <div data-testid="verify-credential-page" className="min-h-[calc(100vh-64px)] bg-[#F4F5F7] py-10 px-4">
      <div className="max-w-xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-[10px] font-black text-[#0B1F40]/40 hover:text-[#E50B15] tracking-[0.15em] uppercase mb-6">
          <ArrowLeft className="h-3 w-3" /> INICIO
        </Link>

        <div className="mb-6">
          <p className="text-[10px] font-black text-[#E50B15] tracking-[0.3em] uppercase mb-1">VERIFICACIÓN OFICIAL</p>
          <h1 className="text-3xl sm:text-4xl font-black text-[#0B1F40] tracking-tight leading-none" style={{ fontFamily: 'Archivo Black' }}>
            CREDENCIAL RP NOTICIAS
          </h1>
          <p className="text-xs text-[#0B1F40]/60 mt-2">
            Comprueba la autenticidad de una credencial oficial de RP Noticias ingresando el código (p. ej. <span className="font-mono font-bold">RP-ABCDEF12</span>).
          </p>
        </div>

        {/* Search form */}
        <form onSubmit={handleSubmit} className="flex gap-2 mb-8">
          <input
            data-testid="verify-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value.toUpperCase())}
            placeholder="RP-XXXXXXXX"
            maxLength={20}
            className="flex-1 bg-white border-2 border-[#0B1F40] px-4 h-11 text-sm font-mono font-bold text-[#0B1F40] tracking-wider placeholder:text-[#0B1F40]/30 focus:outline-none focus:shadow-[3px_3px_0_#E50B15] transition-shadow"
          />
          <button
            data-testid="verify-submit"
            type="submit"
            disabled={loading || !input.trim()}
            className="bg-[#E50B15] text-white text-[10px] font-black h-11 px-5 flex items-center gap-1.5 brutal-btn-press disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ boxShadow: '3px 3px 0 #0B1F40' }}
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
            VERIFICAR
          </button>
        </form>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-7 w-7 animate-spin text-[#E50B15]" />
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div data-testid="verify-error" className="border-2 border-[#E50B15] bg-white p-4 mb-4" style={{ boxShadow: '3px 3px 0 #0B1F40' }}>
            <p className="text-[11px] font-black text-[#E50B15] tracking-[0.15em] uppercase">ERROR</p>
            <p className="text-sm text-[#0B1F40] mt-1">{error}</p>
          </div>
        )}

        {/* Result */}
        {result && !loading && (
          <div
            data-testid={valid ? 'verify-valid' : (notFound ? 'verify-not-found' : 'verify-expired')}
            className={`bg-white border-2 ${valid ? 'border-[#0B1F40]' : 'border-[#E50B15]'}`}
            style={{ boxShadow: valid ? '6px 6px 0 #0B1F40' : '6px 6px 0 #E50B15' }}
          >
            {/* Status bar */}
            <div className={`${valid ? 'bg-[#0B1F40]' : 'bg-[#E50B15]'} px-5 py-4 flex items-center gap-3`}>
              {valid ? (
                <ShieldCheck className="h-7 w-7 text-white shrink-0" />
              ) : (
                <ShieldAlert className="h-7 w-7 text-white shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-white text-lg font-black tracking-tight leading-none" style={{ fontFamily: 'Archivo Black' }}>
                  {valid ? 'CREDENCIAL VÁLIDA' : (notFound ? 'NO ENCONTRADA' : 'CREDENCIAL EXPIRADA')}
                </p>
                <p className="text-white/60 text-[10px] tracking-[0.2em] uppercase mt-1">
                  {valid ? 'Autenticada por RP Noticias' : (notFound ? 'Este código no existe en nuestros registros' : 'Esta credencial ya no es vigente')}
                </p>
              </div>
            </div>

            {/* Details */}
            {(valid || expired) && (
              <div className="p-5 flex gap-4 items-start">
                <div className="w-24 h-28 border-2 border-[#0B1F40] bg-[#F4F5F7] overflow-hidden shrink-0 flex items-center justify-center" style={{ boxShadow: '3px 3px 0 #E50B15' }}>
                  {photoSrc ? (
                    <img src={photoSrc} alt={result.name} className="w-full h-full object-cover" />
                  ) : (
                    <p className="text-[#0B1F40]/20 text-4xl font-black" style={{ fontFamily: 'Archivo Black' }}>
                      {result.name?.charAt(0) || '?'}
                    </p>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[9px] text-[#0B1F40]/40 font-black tracking-[0.25em] uppercase">NOMBRE</p>
                  <p data-testid="verify-name" className="text-lg font-black text-[#0B1F40] leading-tight uppercase mb-3" style={{ fontFamily: 'Archivo Black' }}>
                    {result.name}
                  </p>
                  <p className="text-[9px] text-[#0B1F40]/40 font-black tracking-[0.25em] uppercase">CARGO</p>
                  <p className="inline-block bg-[#E50B15] text-white text-[10px] font-black tracking-[0.15em] px-2 py-1 mb-3">
                    {roleLabel}
                  </p>
                  <p className="text-[9px] text-[#0B1F40]/40 font-black tracking-[0.25em] uppercase">IDENTIFICACIÓN</p>
                  <p data-testid="verify-code" className="text-sm font-black text-[#0B1F40] font-mono tracking-wider mb-3">
                    {result.user_id_code}
                  </p>
                  <div className="grid grid-cols-2 gap-3 border-t-2 border-[#0B1F40]/10 pt-3 mt-1">
                    <div>
                      <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase">EMISIÓN</p>
                      <p className="text-xs font-black text-[#0B1F40]">{formatDate(result.issued_at)}</p>
                    </div>
                    <div>
                      <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase">VIGENCIA</p>
                      <p className={`text-xs font-black ${expired ? 'text-[#E50B15] line-through' : 'text-[#E50B15]'}`}>
                        {formatDate(result.expires_at)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {notFound && (
              <div className="p-5 text-center">
                <p className="text-sm text-[#0B1F40]/70">
                  El código <span className="font-mono font-bold text-[#E50B15]">{code}</span> no corresponde a ninguna credencial emitida por RP Noticias.
                </p>
                <p className="text-xs text-[#0B1F40]/50 mt-3">
                  Revisa el código impreso en la credencial o escanea nuevamente el código QR.
                </p>
              </div>
            )}
          </div>
        )}

        <p className="text-center text-[9px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase mt-8">
          RP NOTICIAS · LA CRÍTICA DE LOS HECHOS
        </p>
      </div>
    </div>
  );
}
